import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

export type GranularityUnit =
  'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

/**
 * Derive the rounding granularity from an ISO 8601 period string.
 *
 * The least-significant component that is explicitly written (even if its
 * value is 0) determines the granularity.  Examples:
 *   P1D      → 'day'
 *   P1Y2M    → 'month'  (month is more granular than year)
 *   PT1H     → 'hour'
 *   PT1H0M   → 'minute'  (0M is explicitly present)
 *   PT0H0M0S → 'second'
 *   P1DT6H   → 'hour'   (time section takes precedence over date section)
 *
 * Throws if `period` is not a valid ISO 8601 duration (fails isDuration check).
 */
export const parsePeriodGranularity = (period: string): GranularityUnit => {
  if (!isDuration(period)) {
    throw new Error(
      `parsePeriodGranularity: "${period}" is not a valid ISO 8601 period`,
    );
  }
  const trimmed = period.trim().replace(/^-/, '');
  const tIdx = trimmed.indexOf('T');
  const datePart = tIdx >= 0 ? trimmed.slice(1, tIdx) : trimmed.slice(1);
  const timePart = tIdx >= 0 ? trimmed.slice(tIdx + 1) : '';

  let unit: GranularityUnit = 'day'; // default for date-only periods

  if (/\d+Y/.test(datePart)) unit = 'year';
  if (/\d+M/.test(datePart)) unit = 'month';
  if (/\d+D/.test(datePart)) unit = 'day';

  if (timePart) {
    if (/\d+H/.test(timePart)) unit = 'hour';
    if (/\d+M/.test(timePart)) unit = 'minute';
    if (/\d+S/.test(timePart)) unit = 'second';
  }

  return unit;
};

/**
 * Round a date up to the next calendar boundary at the given granularity.
 * If the date is already exactly on a boundary it is returned unchanged.
 *
 * Boundaries are evaluated in the timezone of the dayjs object.  Pass a
 * UTC dayjs (dayjs.utc(...)) for UTC-aligned boundaries.
 *
 * Examples (UTC input):
 *   roundUpToGranularity(2025-04-24T10:35:00Z, 'day')    → 2025-04-25T00:00:00Z
 *   roundUpToGranularity(2025-04-24T10:35:00Z, 'hour')   → 2025-04-24T11:00:00Z
 *   roundUpToGranularity(2025-04-24T10:00:00Z, 'hour')   → 2025-04-24T10:00:00Z (on boundary)
 */
export const roundUpToGranularity = (
  date: Dayjs,
  unit: GranularityUnit,
): Dayjs => {
  const djUnit = unit as ManipulateType;
  const boundary = date.startOf(djUnit);
  if (boundary.isSame(date)) return date;
  return boundary.add(1, djUnit);
};

/**
 * Round a date down to the current calendar boundary at the given granularity
 * (i.e. truncate). Always returns a value ≤ the input.
 *
 * Boundaries are evaluated in the timezone of the dayjs object.  Pass a
 * UTC dayjs (dayjs.utc(...)) for UTC-aligned boundaries.
 *
 * Examples (UTC input):
 *   roundDownToGranularity(2025-04-24T10:35:00Z, 'day')  → 2025-04-24T00:00:00Z
 *   roundDownToGranularity(2025-04-24T10:35:00Z, 'hour') → 2025-04-24T10:00:00Z
 *   roundDownToGranularity(2025-04-24T10:00:00Z, 'hour') → 2025-04-24T10:00:00Z (on boundary)
 */
export const roundDownToGranularity = (
  date: Dayjs,
  unit: GranularityUnit,
): Dayjs => date.startOf(unit as ManipulateType);

/**
 * Check if a string is an ISO 8601 duration (starts with P or -P)
 */
export const isDuration = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  return /^-?P/.test(value.trim());
};

/**
 * Parse an ISO 8601 duration string and return a dayjs duration object.
 * Supports negative durations prefixed with '-' (e.g., '-P1D' for 1 day ago)
 */
export const parseDuration = (
  value: string,
): { duration: duration.Duration; negative: boolean } => {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const durationStr = negative ? trimmed.slice(1) : trimmed;
  return {
    duration: dayjs.duration(durationStr),
    negative,
  };
};

/**
 * Round a dayjs time to the nearest interval (in milliseconds).
 * @param time - The time to round
 * @param intervalMs - The interval in milliseconds to round to
 * @param mode - 'floor' (default), 'ceil', or 'round'
 */
export const roundTime = (
  time: Dayjs,
  intervalMs: number,
  mode: 'floor' | 'ceil' | 'round' = 'floor',
): Dayjs => {
  if (!intervalMs || intervalMs <= 0) return time;

  const ms = time.valueOf();
  let rounded: number;

  switch (mode) {
    case 'ceil':
      rounded = Math.ceil(ms / intervalMs) * intervalMs;
      break;
    case 'round':
      rounded = Math.round(ms / intervalMs) * intervalMs;
      break;
    case 'floor':
    default:
      rounded = Math.floor(ms / intervalMs) * intervalMs;
      break;
  }

  return dayjs(rounded);
};

/**
 * Parse a time value that can be either:
 * - An ISO 8601 date-time string (e.g., '2025-01-01T00:00:00Z')
 * - An ISO 8601 duration relative to now (e.g., 'PT0H', '-P1D', 'P7D')
 *
 * When `granularityUnit` is supplied it takes precedence over `roundIntervalMs`
 * and applies calendar-aware rounding — only to duration-based values, not to
 * absolute date strings:
 *   - Positive duration → ceil  (roundUpToGranularity) — snap forward
 *   - Negative duration → floor (roundDownToGranularity) — snap backward
 *
 * @param value - The time string to parse
 * @param reference - Reference time for duration calculations (default: now)
 * @param roundIntervalMs - Optional ms interval for legacy floor-rounding
 * @param granularityUnit - Optional calendar unit for granularity rounding
 */
export const parseTimeValue = (
  value: string,
  reference: Dayjs = dayjs(),
  roundIntervalMs?: number,
  granularityUnit?: GranularityUnit,
): Dayjs => {
  let result: Dayjs;
  const isDur = isDuration(value);
  let negative = false;

  if (isDur) {
    const { duration: dur, negative: isNeg } = parseDuration(value);
    negative = isNeg;
    result = isNeg ? reference.subtract(dur) : reference.add(dur);
  } else {
    result = dayjs(value);
  }

  if (isDur && granularityUnit) {
    result = negative
      ? roundDownToGranularity(result, granularityUnit)
      : roundUpToGranularity(result, granularityUnit);
  } else if (roundIntervalMs && roundIntervalMs > 0) {
    result = roundTime(result, roundIntervalMs);
  }

  return result;
};

/**
 * Parse a time range where each value can be a date-time or duration.
 *
 * @param range - Array of two time strings [start, end]
 * @param reference - Reference time for duration calculations (default: now)
 * @param roundIntervalMs - Optional ms interval for legacy floor-rounding
 * @param granularityUnit - Optional calendar unit for ceil-rounding (takes precedence)
 */
export const parseTimeRange = (
  range: [string, string],
  reference: Dayjs = dayjs(),
  roundIntervalMs?: number,
  granularityUnit?: GranularityUnit,
): [Dayjs, Dayjs] => {
  return [
    parseTimeValue(range[0], reference, roundIntervalMs, granularityUnit),
    parseTimeValue(range[1], reference, roundIntervalMs, granularityUnit),
  ];
};

/**
 * Resolve the start of a time control's range, applying the same rounding the
 * TimeSlider applies when it parses its range (calendar-granularity rounding
 * when `step` is an ISO 8601 duration string, legacy ms-interval rounding
 * otherwise), so a time defaulted from the range start lands exactly on the
 * slider's first tick. Returns null when the control defines no range.
 *
 * @param timeControl - The timeControl spec ({range, step}), if any
 * @param reference - Reference time for duration calculations (default: now)
 */
export const timeControlStart = (
  timeControl:
    { range?: [string, string]; step?: number | string } | null | undefined,
  reference: Dayjs = dayjs(),
): Dayjs | null => {
  const start = timeControl?.range?.[0];
  if (!start) return null;
  const step = timeControl.step;
  if (step && typeof step === 'string' && isDuration(step)) {
    return parseTimeValue(
      start,
      reference,
      undefined,
      parsePeriodGranularity(step),
    );
  }
  return parseTimeValue(
    start,
    reference,
    step ? parseIncrement(step) : undefined,
  );
};

/**
 * Parse increment value which can be:
 * - A number (seconds)
 * - An ISO 8601 duration string (e.g., 'PT1H', 'P1D')
 *
 * @returns Interval in milliseconds
 */
export const parseIncrement = (increment: number | string): number => {
  if (typeof increment === 'number') {
    return increment * 1000; // Convert seconds to milliseconds
  }
  if (typeof increment === 'string' && isDuration(increment)) {
    const { duration: dur } = parseDuration(increment);
    return dur.asMilliseconds();
  }
  return 0;
};

/** A tick interval expressed as a whole count of a calendar unit. */
export interface TimeInterval {
  count: number;
  unit: GranularityUnit;
}

/** A positioned, labelled tick mark for a time axis/slider. */
export interface TimeMark {
  value: number; // epoch milliseconds
  label: string;
}

// Approximate millisecond span of each unit. Used ONLY to choose an interval;
// actual tick placement is calendar/timezone-aware (see alignedTimeTicks).
const UNIT_MS: Record<GranularityUnit, number> = {
  second: 1000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  month: 2_629_800_000, // 30.4375 days
  year: 31_557_600_000, // 365.25 days
};

// Human-friendly tick intervals, ascending. Sub-day counts divide their unit
// evenly (so they stay aligned to the clock); month counts divide the year and
// year counts are round multiples. The ladder is split at the day boundary so a
// multi-day range gets start-of-day stops rather than being subdivided into
// 6h/12h slices (see niceTimeInterval).
const SUBDAY_LADDER: TimeInterval[] = [
  { count: 1, unit: 'second' },
  { count: 5, unit: 'second' },
  { count: 15, unit: 'second' },
  { count: 30, unit: 'second' },
  { count: 1, unit: 'minute' },
  { count: 2, unit: 'minute' },
  { count: 5, unit: 'minute' },
  { count: 10, unit: 'minute' },
  { count: 15, unit: 'minute' },
  { count: 30, unit: 'minute' },
  { count: 1, unit: 'hour' },
  { count: 2, unit: 'hour' },
  { count: 3, unit: 'hour' },
  { count: 6, unit: 'hour' },
  { count: 12, unit: 'hour' },
];

const DAYPLUS_LADDER: TimeInterval[] = [
  { count: 1, unit: 'day' },
  { count: 2, unit: 'day' },
  { count: 7, unit: 'day' },
  { count: 1, unit: 'month' },
  { count: 3, unit: 'month' },
  { count: 1, unit: 'year' },
  { count: 2, unit: 'year' },
  { count: 5, unit: 'year' },
  { count: 10, unit: 'year' },
];

const intervalMs = (i: TimeInterval): number => i.count * UNIT_MS[i.unit];

/**
 * Choose a human-sensible tick interval for a time span.
 *
 * Spans under two days use sub-day steps (minutes/hours, aligned to the clock);
 * longer spans step in whole days/months/years, so a multi-day range lands on
 * the start of each day rather than being subdivided. Within the chosen ladder
 * the smallest step whose span is ≥ span / targetCount wins, keeping the tick
 * count ≤ ~targetCount on round, readable values (e.g. 15 min, 6 h, 1 day).
 */
export const niceTimeInterval = (
  spanMs: number,
  targetCount = 6,
): TimeInterval => {
  const ideal = spanMs / Math.max(targetCount, 1);
  const ladder = spanMs < 2 * UNIT_MS.day ? SUBDAY_LADDER : DAYPLUS_LADDER;
  for (const step of ladder) {
    if (intervalMs(step) >= ideal) return step;
  }
  return ladder[ladder.length - 1];
};

/**
 * Generate tick positions (epoch ms) across [startMs, endMs], aligned to
 * calendar/clock boundaries in the given IANA timezone.
 *
 * Sub-day intervals are anchored at local midnight so ticks land on round clock
 * values (00:00, 06:00, 12:00 …); day intervals on local midnight, months on
 * the 1st, years on Jan 1 — all evaluated in `tz`, so they stay correct across
 * DST transitions (a "day" is local midnight, not a fixed ms multiple). Returns
 * [] for a non-positive span.
 */
export const alignedTimeTicks = (
  startMs: number,
  endMs: number,
  tz = 'UTC',
  targetCount = 6,
  interval?: TimeInterval,
): number[] => {
  if (!(endMs > startMs)) return [];
  const { count, unit } =
    interval ?? niceTimeInterval(endMs - startMs, targetCount);
  const start = dayjs(startMs).tz(tz);
  const stepUnit = unit as ManipulateType;

  // Calendar boundary at/below startMs, snapped to a multiple of `count` where
  // that keeps ticks on round values. Day-based ticks (incl. 7-day) anchor on
  // local midnight rather than startOf('week') so positions don't depend on the
  // runtime locale's first-day-of-week.
  let anchor: Dayjs;
  switch (unit) {
    case 'second':
    case 'minute':
    case 'hour':
    case 'day':
      anchor = start.startOf('day');
      break;
    case 'month':
      anchor = start
        .startOf('year')
        .add(Math.floor(start.month() / count) * count, 'month');
      break;
    case 'year':
      anchor = start
        .startOf('year')
        .year(Math.floor(start.year() / count) * count);
      break;
  }

  // Re-anchor calendar-unit steps to the local boundary: dayjs add() works in
  // absolute time, so adding "1 day" across a DST transition would drift a tick
  // an hour off local midnight. Sub-day steps keep their absolute offset (they
  // are clock-aligned within the day by construction).
  const snap = (d: Dayjs): Dayjs => {
    if (unit === 'day') return d.startOf('day');
    if (unit === 'month') return d.startOf('month');
    if (unit === 'year') return d.startOf('year');
    return d;
  };
  const stepOnce = (d: Dayjs): Dayjs => snap(d.add(count, stepUnit));

  // Fast-forward most of the way (calendar-correct add), under-shooting by a
  // step so the while loop lands exactly on the first tick ≥ startMs.
  let t = anchor;
  if (t.valueOf() < startMs) {
    const jumps =
      Math.floor((startMs - t.valueOf()) / intervalMs({ count, unit })) - 1;
    if (jumps > 0) t = snap(t.add(jumps * count, stepUnit));
  }
  let guard = 0;
  while (t.valueOf() < startMs && guard++ < 100_000) t = stepOnce(t);

  const ticks: number[] = [];
  guard = 0;
  while (t.valueOf() <= endMs && guard++ < 100_000) {
    ticks.push(t.valueOf());
    t = stepOnce(t);
  }
  return ticks;
};

/**
 * Format a tick label appropriate to the interval and timezone.
 *
 * Year/month/day intervals get date-only labels; sub-day intervals get a time
 * label, with the date prepended on the first tick and whenever the local day
 * rolls over — so a multi-tick day reads cleanly without repeating the date.
 */
export const formatTimeMark = (
  valueMs: number,
  interval: TimeInterval,
  tz = 'UTC',
  prevMs: number | null = null,
): string => {
  const d = dayjs(valueMs).tz(tz);
  switch (interval.unit) {
    case 'year':
      return d.format('YYYY');
    case 'month':
      return d.format('YYYY-MM');
    case 'day':
      return d.format('YYYY-MM-DD');
    default: {
      const timeFmt = interval.unit === 'second' ? 'HH:mm:ss' : 'HH:mm';
      const newDay = prevMs == null || !dayjs(prevMs).tz(tz).isSame(d, 'day');
      return d.format(newDay ? `MM-DD ${timeFmt}` : timeFmt);
    }
  }
};

/**
 * Compute slider/axis tick marks for a time range: timezone-aware positions on
 * sensible intervals (see alignedTimeTicks), each with a range-appropriate
 * label (see formatTimeMark). The label format is chosen from the total span,
 * so a multi-day range shows dates and a sub-day range shows times.
 */
/**
 * Pick a timeMarks target count that fits an available track width, so wide
 * sliders get more labels and narrow (phone) sliders get fewer — instead of a
 * fixed target with position-based CSS hiding on top.
 *
 * Two-pass: guess the interval family from a generic slot width, then size
 * the slot for that family's label width (date labels are ~2x wider than
 * time-of-day labels). Falls back to the historical default of 6 when the
 * width is unknown (e.g. before first measure).
 */
export const fitTimeMarkCount = (trackPx: number, spanMs: number): number => {
  if (!trackPx || trackPx <= 0 || !spanMs || spanMs <= 0) return 6;
  const slotFor = (i: TimeInterval) =>
    i.unit === 'day' || i.unit === 'month' || i.unit === 'year' ? 95 : 70;
  const guess = niceTimeInterval(spanMs, Math.max(2, Math.floor(trackPx / 80)));
  return Math.min(12, Math.max(2, Math.floor(trackPx / slotFor(guess))));
};

export const timeMarks = (
  startMs: number,
  endMs: number,
  tz = 'UTC',
  targetCount = 6,
): TimeMark[] => {
  // Compute the interval once and share it with tick placement, so positions
  // and labels can never disagree.
  const interval = niceTimeInterval(endMs - startMs, targetCount);
  let prev: number | null = null;
  return alignedTimeTicks(startMs, endMs, tz, targetCount, interval).map(
    (value) => {
      const label = formatTimeMark(value, interval, tz, prev);
      prev = value;
      return { value, label };
    },
  );
};
